// Simple reusable hook to manage a number input that allows temporary empty text
// and normalizes/clamps on blur or explicit submission.
// Usage:
// const num = useEditableNumber(5, { min: 1, max: 1000 });
// <input {...num.bind} type="number" min={1} max={1000} step={1} />
// const finalValue = num.normalize(); // parse & clamp current input, update state

import { useCallback, useMemo, useState } from 'react';

type Options = {
  min?: number;
  max?: number;
  integer?: boolean;
};

export function useEditableNumber(initial: number, opts: Options = {}) {
  const { min, max, integer = false } = opts;
  const [value, setValue] = useState<number>(initial);
  const [input, setInput] = useState<string>(String(initial ?? ''));

  const clamp = useCallback(
    (n: number) => {
      if (integer) n = Math.round(n);
      if (typeof min === 'number') n = Math.max(min, n);
      if (typeof max === 'number') n = Math.min(max, n);
      return n;
    },
    [min, max, integer],
  );

  const apply = useCallback(
    (n: number) => {
      const clamped = clamp(n);
      setValue(clamped);
      setInput(String(clamped));
      return clamped;
    },
    [clamp],
  );

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const onBlur = useCallback(() => {
    const parsed = parseFloat(input);
    if (Number.isFinite(parsed)) {
      apply(parsed);
    } else {
      // revert to last valid numeric value
      setInput(String(value));
    }
  }, [input, value, apply]);

  const normalize = useCallback(() => {
    const parsed = parseFloat(input);
    if (Number.isFinite(parsed)) {
      return apply(parsed);
    }
    // invalid → return current numeric state (already valid)
    setInput(String(value));
    return value;
  }, [input, value, apply]);

  const bind = useMemo(
    () => ({ value: input, onChange, onBlur }),
    [input, onChange, onBlur],
  );

  return {
    value,
    input,
    setValue: apply,
    setInput,
    bind,
    normalize,
  };
}
